import { type HTMLAttributes } from "react";
import { cn } from "../../lib/cn";

type MeterTone = "good" | "neutral" | "warn" | "danger";

const meterToneClassNames: Record<MeterTone, string> = {
  good: "ui-meter-tone-good",
  neutral: "ui-meter-tone-neutral",
  warn: "ui-meter-tone-warn",
  danger: "ui-meter-tone-danger"
};

export interface MeterProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  label: string;
  showLabel?: boolean;
  value: number;
  valueText: string;
  tone?: MeterTone;
}

export function Meter({ label, showLabel = false, value, valueText, tone = "good", className, ...props }: MeterProps) {
  const clampedValue = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  const meter = (
    <span
      className={cn("ui-meter", meterToneClassNames[tone], className)}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clampedValue}
      aria-valuetext={valueText}
      {...props}
    >
      <span className="ui-meter-fill" style={{ width: `${clampedValue}%` }} />
    </span>
  );

  if (!showLabel) return meter;

  return (
    <span className="ui-meter-field">
      <span className="ui-meter-label">
        <span>{label}</span>
        <strong>{valueText}</strong>
      </span>
      {meter}
    </span>
  );
}
